// src/App.js
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Link, Redirect, useLocation } from 'react-router-dom';
import Signup from './pages/Signup';
import Login from './pages/Login';
import ControlPanel from './pages/ControlPanel';
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import SubscriptionPage from './pages/Subscriptions';
import BillingIntake from './pages/BillingIntake/BillingIntake';
import PublicMarkdownPage from './pages/PublicMarkdownPage';
import GigSubmission from './pages/GigSubmission';
import RemoteApp from './pages/RemoteApp';
import ConsoleApp from './pages/ConsoleApp';
import PublicFooter from './components/PublicFooter';
import { SocketProvider } from './context/SocketContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { NudgeProvider } from './context/NudgeContext';
import axios from 'axios';
import { trackWebVisit } from './utils/analytics';
import { initGA4, sendPageView } from './utils/ga4';
import { initFbPixel, trackPageView as trackFbPageView } from './utils/fbPixel';
import { initClarity } from './utils/clarity';
import { initTiktokPixel, trackTiktokPageView } from './utils/tiktokPixel';

// Helper function to check if the user is authenticated
const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Define the PrivateRoute component
const PrivateRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <SocketProvider isAuthenticated={isAuthenticated}>
            <EntitlementsProvider>
              <NudgeProvider>
                <Component {...props} />
              </NudgeProvider>
            </EntitlementsProvider>
          </SocketProvider>
        ) : (
          <Redirect to="/login" />
        )
      }
    />
  );
};

function App() {
  const [auth, setAuth] = useState({
    isAuthenticated: isAuthenticated(),
  });

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    setAuth({ isAuthenticated: !!token });
  }, []);

  // Axios interceptor (mounted once, cleaned up on unmount)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          handleLogout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    setAuth({ isAuthenticated: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAuth({ isAuthenticated: false });
  };

  return (
    <Router>
      <ScrollManager />
      <AppShell auth={auth} handleLogin={handleLogin} handleLogout={handleLogout} />
    </Router>
  );
}

function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history?.scrollRestoration) return undefined;
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    const frameId = window.requestAnimationFrame(scrollToTop);

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, location.search]);

  return null;
}

function AppShell({ auth, handleLogin, handleLogout }) {
  const location = useLocation();
  const isGigSubmission = location.pathname.startsWith('/gig-submissions/');
  const isBillingIntake = location.pathname.startsWith('/billing-intake/');
  const isRemoteSurface = location.pathname.startsWith('/remote') || location.pathname.startsWith('/console');
  const hideNav = ['/login', '/signup', '/onboarding'].includes(location.pathname) || isGigSubmission || isBillingIntake || isRemoteSurface;
  const isControlPanel = location.pathname === '/control-panel';
  const hideFooter = hideNav || isControlPanel || isGigSubmission || isBillingIntake;
  const [gaReady, setGaReady] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const [tiktokReady, setTiktokReady] = useState(false);
  const [gaSiteId, setGaSiteId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('siteId');
  });
  const travelpayoutsScriptRef = useRef('');
  const getyourguideScriptRef = useRef('');

  const applyTravelpayoutsScript = useCallback((html) => {
    if (typeof document === 'undefined') return;
    const normalized = String(html || '').trim();
    const existing = document.querySelectorAll('[data-travelpayouts-script="1"]');
    existing.forEach((node) => node.parentNode?.removeChild(node));
    travelpayoutsScriptRef.current = normalized;
    if (!normalized) return;
    const template = document.createElement('template');
    template.innerHTML = normalized;
    Array.from(template.content.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SCRIPT') {
        const script = document.createElement('script');
        Array.from(node.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });
        script.dataset.travelpayoutsScript = '1';
        if (node.textContent) {
          script.textContent = node.textContent;
        }
        document.head.appendChild(script);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node.cloneNode(true);
        el.dataset.travelpayoutsScript = '1';
        document.head.appendChild(el);
      }
    });
  }, []);

  const applyGetYourGuideScript = useCallback((html) => {
    if (typeof document === 'undefined') return;
    const normalized = String(html || '').trim();
    const existing = document.querySelectorAll('[data-getyourguide-script="1"]');
    existing.forEach((node) => node.parentNode?.removeChild(node));
    getyourguideScriptRef.current = normalized;
    if (!normalized) return;
    const template = document.createElement('template');
    template.innerHTML = normalized;
    Array.from(template.content.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SCRIPT') {
        const script = document.createElement('script');
        Array.from(node.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });
        script.dataset.getyourguideScript = '1';
        if (node.textContent) {
          script.textContent = node.textContent;
        }
        document.head.appendChild(script);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node.cloneNode(true);
        el.dataset.getyourguideScript = '1';
        document.head.appendChild(el);
      }
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedSiteId = localStorage.getItem('siteId');
    if (storedSiteId !== gaSiteId) {
      setGaSiteId(storedSiteId);
    }
  }, [location.pathname, gaSiteId]);

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      const platformId = process.env.REACT_APP_PLATFORM_GA4_ID;
      const platformFbId = process.env.REACT_APP_PLATFORM_FB_PIXEL_ID;
      const platformClarityId = process.env.REACT_APP_PLATFORM_CLARITY_ID;
      const platformTiktokId = process.env.REACT_APP_PLATFORM_TIKTOK_PIXEL_ID;
      let customId = null;
      let customFbId = null;
      let customClarityId = null;
      let customTiktokId = null;
      const token = localStorage.getItem('token');

      if (token && gaSiteId) {
        try {
          const res = await fetch(`/api/sites/${gaSiteId}/analytics`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            customId = data?.gaMeasurementId || null;
            customFbId = data?.fbPixelId || null;
            customClarityId = data?.clarityProjectId || null;
            customTiktokId = data?.tiktokPixelId || null;
          }
        } catch (err) {
          console.error('Failed to load GA4 settings', err);
        }
      }

      if (!isMounted) return;
      const hasIds = Boolean(platformId || customId);
      const hasFbIds = Boolean(platformFbId || customFbId);
      if (hasIds) {
        initGA4(platformId, customId);
      }
      if (hasFbIds) {
        initFbPixel(platformFbId, customFbId);
      }
      const clarityId = customClarityId || platformClarityId;
      if (clarityId) {
        initClarity(clarityId);
      }
      const hasTiktokIds = Boolean(platformTiktokId || customTiktokId);
      if (hasTiktokIds) {
        initTiktokPixel(platformTiktokId, customTiktokId);
      }
      setGaReady(hasIds);
      setFbReady(hasFbIds);
      setTiktokReady(hasTiktokIds);
    };

    init();
    return () => {
      isMounted = false;
    };
  }, [auth.isAuthenticated, gaSiteId]);

  useEffect(() => {
    let active = true;
    const loadPlatformScripts = async () => {
      if (typeof window === 'undefined') return;
      if (location.pathname !== '/') {
        applyTravelpayoutsScript('');
        applyGetYourGuideScript('');
        return;
      }
      try {
        const res = await fetch('/api/platform-settings/public');
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        applyTravelpayoutsScript(data?.travelpayoutsHeadScript || '');
        applyGetYourGuideScript(data?.getyourguideHeadScript || '');
      } catch (err) {
        console.error('Failed to load Travelpayouts script', err);
      }
    };
    loadPlatformScripts();
    return () => {
      active = false;
    };
  }, [applyTravelpayoutsScript, applyGetYourGuideScript, location.pathname]);

  useEffect(() => {
    trackWebVisit({ path: location.pathname });
    if (gaReady) {
      sendPageView(location.pathname);
    }
    if (fbReady) {
      trackFbPageView();
    }
    if (tiktokReady) {
      trackTiktokPageView();
    }
  }, [fbReady, gaReady, location.pathname, tiktokReady]);

  const ConfirmSiteRedirect = ({ match }) => {
    const params = new URLSearchParams();
    params.set('tab', 'Settings');
    params.set('settingsSubTab', 'Site Details');
    params.set('siteId', match?.params?.siteId);
    params.set('placeId', match?.params?.placeId);
    return <Redirect to={`/control-panel?${params.toString()}`} />;
  };

  return (
    <>
      {!hideNav && (
        <nav className={`top-nav${isControlPanel ? ' top-nav--control-panel' : ''}`}>
          <ul>
            {!auth.isAuthenticated ? (
              <>
                <li className="nav-brand"><Link to="/">Suitestream</Link></li>
                <li className="nav-spacer" />
                <li><Link to="/subscription">Pricing</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li>
                  <Link to="/login" className="nav-link-signin">Sign In</Link>
                </li>
                <li>
                  <Link to="/signup" className="nav-btn-cta">Start Free</Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  {isControlPanel ? (
                    <span className="nav-title">Control Panel</span>
                  ) : (
                    <Link to="/control-panel">Control Panel</Link>
                  )}
                </li>
                <li className="nav-spacer" />
                <li><Link to="/" onClick={handleLogout}>Sign Out</Link></li>
              </>
            )}
          </ul>
        </nav>
      )}

      <Switch>
        <Route path="/" exact component={Home} />
        <Route path="/privacy" render={() => <PublicMarkdownPage src="/privacy.md" title="Privacy Policy" />} />
        <Route path="/terms" render={() => <PublicMarkdownPage src="/terms.md" title="Terms of Service" />} />
        <Route path="/about" render={() => <PublicMarkdownPage src="/about.md" title="About Suitestream" />} />
        <Route path="/contact" render={() => <PublicMarkdownPage src="/contact.md" title="Contact Us" />} />
        <Route path="/subscription" exact component={SubscriptionPage} />
        <Route path="/subscriptions" exact component={SubscriptionPage} />
        <Route path="/billing-intake/:token" component={BillingIntake} />
        <Route path="/gig-submissions/:token" component={GigSubmission} />
        <Route path="/remote" component={RemoteApp} />
        <Route path="/console" component={ConsoleApp} />
        <Route path="/signup" component={() => <Signup onSignup={handleLogin} />} />
        <Route path="/login" component={() => <Login onLogin={handleLogin} />} />
        <PrivateRoute
          path="/onboarding"
          component={Onboarding}
          isAuthenticated={auth.isAuthenticated}
        />
        <PrivateRoute
          path="/sites/:siteId/confirm/:placeId"
          component={ConfirmSiteRedirect}
          isAuthenticated={auth.isAuthenticated}
        />
        <PrivateRoute
          path="/control-panel"
          component={ControlPanel}
          isAuthenticated={auth.isAuthenticated}
        />
      </Switch>

      {!hideFooter && <PublicFooter />}
    </>
  );
}

export default App;
